<template>
  <div class="section-overview ov">
    <!-- En-tête : accueil, période, actions rapides -->
    <div class="ov-header">
      <div class="ov-hello">
        <h1 class="ov-title">{{ greeting }}</h1>
        <p class="ov-subtitle">{{ $t('dash.overview.subtitle_v2') }}</p>
      </div>

      <div class="ov-actions">
        <Tooltip v-for="action in quickActions" :key="action.key" :text="$t(`dash.overview.help.action.${action.key}`)" position="bottom">
          <button
            class="ov-action"
            :class="{ 'ov-action--primary': action.primary }"
            @click="go(action.target)"
          >
            <i class="material-icons">{{ action.icon }}</i>
            <span>{{ action.label }}</span>
            <span v-if="action.badge" class="ov-action-badge">{{ action.badge > 99 ? '99+' : action.badge }}</span>
          </button>
        </Tooltip>
      </div>
    </div>

    <!-- À traiter : pastilles compactes, détail dépliable -->
    <OverviewAlerts
      :pages="pagesData"
      :risk-posts="riskPosts"
      :validation-posts="validationPosts"
      :assigned-posts="assignedPosts"
      :inbox-count="inboxAllowed ? inboxCount : 0"
      :inbox-events="inboxEvents"
      :inbox-events-loading="inboxEventsLoading"
      @load-inbox="loadInboxEvents"
      @open-inbox-event="openInboxEvent"
      :can-edit="canEdit"
      :can-reconnect-inline="canReconnectInline"
      :loading="!sideLoaded"
      @reconnect="reconnect"
      @go="go"
      @open-post="openPublishedPost"
    />

    <!-- Dernières publications + à venir (en tête, comme dans l'ancien overview) -->
    <div class="ov-columns">
      <OverviewLastPosts
        :last="overview ? overview.last_posts : null"
        :last-stats="lastPostStats"
        :loading="overviewLoading && !overview"
        @go="go"
        @open-last-post="openPublishedPost"
        @open-network="openOnNetwork"
      />
      <OverviewUpcoming
        :items="upcomingView"
        :loading="upcomingLoading && !upcoming.length"
        :can-edit="canEdit"
        @go="go"
        @open-post="openPublishedPost"
      />
    </div>

    <!-- Statistiques : le choix de période est dans l'en-tête du bloc qu'il pilote -->
    <div class="ov-stats">
      <div class="ov-stats-head">
        <div class="ov-block-title">
          <i class="material-icons ov-title-icon">show_chart</i>
          <h2>{{ $t('dash.overview.kpi.title') }}</h2>
          <Tooltip :text="presetHelp(period)" position="top">
            <span class="ov-chip-muted">{{ periodLabel }}</span>
          </Tooltip>
        </div>
        <div class="ov-toolbar">
          <div class="ov-presets">
            <Tooltip v-for="preset in presets" :key="preset.key" :text="presetHelp(preset)" position="top">
              <button
                class="ov-preset"
                :class="{ active: period.key === preset.key }"
                @click="selectPeriod({ key: preset.key })"
              >{{ $t(preset.label) }}</button>
            </Tooltip>
          </div>
          <div class="ov-period-custom" :class="{ active: period.key === 'custom' }">
            <ReportingDatePicker
              :date-start="customStart"
              :date-end="customEnd"
              compare-mode="none"
              @apply="onCustomPeriod"
            />
          </div>
          <span v-if="overviewLoading && overview" class="ov-updating">{{ $t('dash.overview.updating') }}</span>
          <Tooltip :text="$t('dash.overview.refresh')" position="top" class="ov-toolbar-refresh">
            <button class="ov-refresh" :disabled="overviewLoading" @click="loadOverview(true)">
              <i class="material-icons" :class="{ spin: overviewLoading }">refresh</i>
            </button>
          </Tooltip>
        </div>
      </div>

      <!-- En cas d'erreur, les données en cache restent affichées -->
      <p v-if="overviewError" class="ov-error">
        {{ $t('dash.overview.error') }}
        <button class="ov-arrow-link" @click="loadOverview(true)">{{ $t('dash.overview.retry') }}</button>
      </p>
      <OverviewKpis
        v-if="!overviewError || overview"
        :class="{ 'ov-refreshing': overviewLoading && overview }"
        :data="overview"
        :loading="overviewLoading && !overview"
        :reporting-allowed="reportingAllowed"
        :is-day-granularity="period.key === '24h'"
        @go="name => go({ name })"
      />

      <!-- Top publications + meilleurs créneaux : dépendent aussi de la période -->
      <div class="ov-columns ov-columns--wide-left">
        <OverviewTopPosts
          :top="overview ? overview.top_posts : null"
          :loading="overviewLoading && !overview"
          @open-post="openPublishedPost"
        />
        <OverviewBestTimes
          v-if="reportingAllowed"
          :grid="overview ? overview.best_times : null"
          :loading="overviewLoading && !overview"
        />
      </div>

      <OverviewNetworks
        :per-page="overview ? overview.per_page : null"
        :pages="pagesData"
        :loading="overviewLoading && !overview"
      />
    </div>

    <!-- Santé du compte : connexions des pages, sources de contenu, présence locale -->
    <div class="ov-health">
      <div class="ov-block-title ov-section-title">
        <i class="material-icons ov-title-icon">verified_user</i>
        <h2>{{ $t('dash.overview.health_section.title') }}</h2>
      </div>
      <div class="ov-columns ov-columns--equal">
        <OverviewPages
          :pages="pagesData"
          :per-page="overview ? overview.per_page : null"
          :can-edit="canEdit"
          :reporting-allowed="reportingAllowed"
          :can-reconnect-inline="canReconnectInline"
          :inbox-counts="inboxAllowed ? inboxCounts : {}"
          :next-scheduled="nextScheduledByPage"
          @reconnect="reconnect"
          @go="go"
          @create-post="createPostForPage"
          @open-reporting="openReportingForPage"
          @open-inbox="openInboxForPage"
        />
        <OverviewSources :user-id="cacheUserId()" :can-edit="canEdit" @go="go" />
      </div>
      <OverviewPresence v-if="presenceEnabled" :user-id="cacheUserId()" @go="go" />
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import Posts from '../../mixins/Posts'
import Pages from '../../mixins/Pages'
import User from '../../mixins/User'
import Moderation from '../../mixins/Moderation'
import Insights from '../../mixins/Insights'
import Tooltip from '../tooltip/tooltipInit.vue'
import Popup from '@/utils/Popup'
import { isReportingEligible } from '@/utils/reportingEligibility'
import { readPeriod, writePeriod, readSection, writeSection } from '../overview/cache'
import ReportingDatePicker from '../reporting-web/ReportingDatePicker.vue'
import OverviewKpis from '../overview/OverviewKpis.vue'
import OverviewAlerts from '../overview/OverviewAlerts.vue'
import OverviewPages from '../overview/OverviewPages.vue'
import OverviewUpcoming from '../overview/OverviewUpcoming.vue'
import OverviewTopPosts from '../overview/OverviewTopPosts.vue'
import OverviewLastPosts from '../overview/OverviewLastPosts.vue'
import OverviewBestTimes from '../overview/OverviewBestTimes.vue'
import OverviewNetworks from '../overview/OverviewNetworks.vue'
import OverviewSources from '../overview/OverviewSources.vue'
import OverviewPresence from '../overview/OverviewPresence.vue'
import { PRESETS, DEFAULT_PERIOD, periodRange, loadStoredPeriod, storePeriod } from '../overview/periods'
import { INSIGHTS_NETWORKS, insightsRequest, normalizeInsights } from '../overview/postInsights'

const { VUE_APP_API_HOST } = process.env

// Paramètres d'URL gérés par Dash.vue (retours OAuth, liens profonds) : il va rediriger, on ne charge rien
const CALLBACK_PARAMS = ['code', 'oauth_token', 'test_callback']

export default {
  name: 'overview-v2',

  components: {
    Tooltip,
    ReportingDatePicker,
    OverviewKpis,
    OverviewAlerts,
    OverviewPages,
    OverviewUpcoming,
    OverviewTopPosts,
    OverviewLastPosts,
    OverviewBestTimes,
    OverviewNetworks,
    OverviewSources,
    OverviewPresence
  },

  mixins: [Posts, Pages, Moderation, Insights, User],

  data() {
    const stored = loadStoredPeriod()
    return {
      presets: PRESETS,
      period: stored || { ...DEFAULT_PERIOD },
      periodWasStored: !!stored,
      alive: true,

      pagesData: [],
      overview: null,
      overviewLoading: false,
      overviewError: false,
      overviewRequestId: 0,
      lastPostStats: {},

      upcoming: [],
      upcomingLoading: false,
      // Listes « À traiter » (items au format de toPostItem, ouverts via le lien profond Posts2)
      riskCandidates: [],
      validationPosts: [],
      assignedPosts: [],
      inboxCount: 0,
      inboxCounts: {},
      // Derniers événements Inbox en attente, chargés à l'ouverture du pop-in (20 max)
      inboxEvents: null,
      inboxEventsLoading: false,
      inboxEventsFresh: false,
      // Alertes calculées au moins une fois (cache ou réseau) : évite d'afficher puis masquer le bandeau
      sideLoaded: false
    }
  },

  beforeMount() {
    this.$ga.page(this.$router)
  },

  mounted() {
    // Redirection historique vers les pages (aucun bot, ou connexion LinkedIn en cours) : à garder en premier
    if (this.getUserRole && this.getUserRole != 'Viewer' && (!localStorage.bots || !localStorage.bots.length || localStorage.linkedin_adding_page)) {
      localStorage.setItem('section', 'pages')
      this.$router.push({
        name: 'pages'
      })
      return
    }
    localStorage.setItem('section', 'overview')
    if (this.isRedirectPending()) return
    this.init()
  },

  beforeDestroy() {
    this.alive = false
  },

  computed: {
    currentUser() {
      try {
        return localStorage.user && localStorage.user != 'undefined' ? JSON.parse(localStorage.user) : null
      } catch (e) {
        return null
      }
    },

    getUserRole() {
      const user = this.currentUser
      if (!user) return null
      if (user.admin)
        return 'Admin'
      return user.role && user.role.length ? user.role[0] : 'Editor'
    },

    canEdit() {
      return ['Admin', 'Editor'].includes(this.getUserRole)
    },

    isInternal() {
      return !!(this.currentUser && this.currentUser.email && this.currentUser.email.includes('@mashup-web.com'))
    },

    // Même règle que l'onglet Gestion de présence (Tabs.vue isPresenceManagementEnabled + rôle + plan)
    presenceEnabled() {
      if (!['Admin', 'Editor'].includes(this.getUserRole) || !this.getUserPlan) return false
      if (process.env.NODE_ENV !== 'production') return true
      const user = this.currentUser || {}
      if (user.email && user.email.toLowerCase().endsWith('@mashup-web.com')) return true
      const authorizedAccounts = ['9e046e84-5b26-4c94-8379-9a88ea278490', '6a184790-5f10-43eb-8f16-05daa21d6dc5']
      return authorizedAccounts.includes(user.account) || user.id === '9e046e84-5b26-4c94-8379-9a88ea278490'
    },

    reportingAllowed() {
      return this.checkLock('reporting') || this.isInternal || this.forced('forceReporting')
    },

    inboxAllowed() {
      return this.checkLock('moderation') || this.isInternal || this.forced('forceModeration')
    },

    greeting() {
      const name = this.currentUser && this.currentUser.firstname
      const hour = moment().hour()
      const key = hour < 5 || hour >= 18 ? 'evening' : 'day'
      return `${this.$t(`dash.overview.hello.${key}`)}${name ? `, ${name}` : ''}`
    },

    // À venir enrichi du risque d'échec (page déconnectée, jeton qui expire avant la date)
    upcomingView() {
      return this.upcoming.map(item => ({ ...item, risk: this.riskReason(item) }))
    },

    // Publications planifiées à venir (toutes dates) qui risquent de ne pas partir, les plus proches d'abord
    riskPosts() {
      return this.riskCandidates
        .map(item => ({ ...item, page: this.pagesData.find(p => p.id === item.page_id) || item.page, risk: this.riskReason(item) }))
        .filter(item => item.risk)
        .sort((a, b) => (a.date < b.date ? -1 : 1))
    },

    // id de page -> prochaine publication planifiée ou brouillon daté (liste « À venir », déjà triée)

    nextScheduledByPage() {
      return this.upcoming.reduce((acc, item) => {
        if (item.page_id && !acc[item.page_id]) acc[item.page_id] = item.date
        return acc
      }, {})
    },

    customStart() {
      return this.period.key === 'custom' ? this.period.start : moment().subtract(29, 'days').format('YYYY-MM-DD')
    },

    customEnd() {
      return this.period.key === 'custom' ? this.period.end : moment().format('YYYY-MM-DD')
    },

    periodLabel() {
      if (this.period.key === 'custom')
        return `${moment(this.period.start).format('DD/MM/YYYY')} → ${moment(this.period.end).format('DD/MM/YYYY')}`
      const preset = PRESETS.find(p => p.key === this.period.key)
      return preset ? this.$t(preset.label) : ''
    },

    quickActions() {
      const actions = []
      if (this.canEdit) {
        actions.push({ key: 'create', icon: 'edit', primary: true, label: this.$t('dash.overview.actions.create_post'), target: { name: 'postCreator' } })
      }
      actions.push({ key: 'planner', icon: 'date_range', label: this.$t('dash.overview.actions.planner'), target: { name: 'posts2', tab: 'planner' } })
      if (this.inboxAllowed) {
        actions.push({ key: 'inbox', icon: 'question_answer', label: this.$t('dash.overview.actions.inbox'), target: { name: 'moderation' }, badge: this.inboxCount })
      }
      if (this.reportingAllowed) {
        actions.push({ key: 'reporting', icon: 'insert_chart', label: this.$t('dash.overview.actions.reporting'), target: { name: 'reporting' } })
      }
      if (this.canEdit) {
        actions.push({ key: 'pages', icon: 'add_circle_outline', label: this.$t('dash.overview.actions.add_page'), target: { name: 'pages' } })
      }
      return actions
    }
  },

  methods: {
    // Dates exactes couvertes par une période (Tooltip des boutons de période)
    presetHelp(preset) {
      if (preset.key === 'all') return this.$t('dash.overview.help.period.all')
      const { since, until } = periodRange(preset)
      const fmt = preset.key === '24h' ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY'
      return this.$t('dash.overview.help.period.range', { start: moment.unix(since).format(fmt), end: moment.unix(until).format(fmt) })
    },

    forced(key) {
      try {
        return localStorage[key] ? JSON.parse(localStorage[key]) == true : false
      } catch (e) {
        return false
      }
    },

    isRedirectPending() {
      const usp = new URLSearchParams(location.search)
      if (CALLBACK_PARAMS.some(p => usp.has(p))) return true
      if (usp.has('post_id') && usp.has('page_id')) return true
      if (usp.has('moderation') && usp.has('object_id')) return true
      return !!localStorage.getItem('redirect_params_url')
    },

    async init() {
      this.hydrateFromCache()
      // Les KPI ne dépendent pas des pages : lancés tout de suite, en parallèle
      this.loadOverview()
      await this.loadPages()
      if (!this.alive) return
      this.loadPostsAlerts()
      this.loadInboxCount()
    },

    cacheUserId() {
      return this.currentUser && this.currentUser.id
    },

    // Dernières données connues affichées immédiatement, remplacées à l'arrivée des données fraîches
    hydrateFromCache() {
      const userId = this.cacheUserId()
      if (!this.pagesData.length) this.pagesData = this.cachedPages()
      const cached = readPeriod(userId, this.period)
      if (cached) this.overview = cached

      const side = readSection(userId, 'side')
      if (side) {
        this.upcoming = (side.upcoming || []).map(item => ({ ...item, page: this.pagesData.find(p => p.id === item.page_id) || this.cachedPage(item.page_id) }))
        const withPage = list => (list || []).map(item => ({ ...item, page: this.pagesData.find(p => p.id === item.page_id) || this.cachedPage(item.page_id) }))
        this.riskCandidates = withPage(side.riskCandidates)
        this.validationPosts = withPage(side.validationPosts)
        this.assignedPosts = withPage(side.assignedPosts)
        this.sideLoaded = true
      }

      const inbox = readSection(userId, 'inbox')
      if (inbox) {
        this.inboxCount = inbox.count || 0
        this.inboxCounts = inbox.counts || {}
      }

      // Derniers événements Inbox : affichés depuis le cache, rafraîchis à l'ouverture du pop-in
      const inboxEvents = readSection(userId, 'inboxEvents')
      if (inboxEvents) this.inboxEvents = inboxEvents.map(item => ({ ...item, page: this.pagesData.find(p => p.id === item.page_id) || this.cachedPage(item.page_id) }))

      const stats = readSection(userId, 'stats')
      if (stats) this.lastPostStats = Object.fromEntries(Object.entries(stats).map(([k, metrics]) => [k, { loading: false, metrics }]))
    },

    cachedPages() {
      try {
        return localStorage.pages && localStorage.pages != 'undefined' ? JSON.parse(localStorage.pages) : []
      } catch (e) {
        return []
      }
    },

    cachedPage(pageId) {
      return this.cachedPages().find(p => p.id === pageId) || null
    },

    /**
     * Pages accessibles avec leur état de connexion (bot_status)
     */
    async loadPages() {
      const fromCache = () => {
        try {
          return localStorage.pages && localStorage.pages != 'undefined' ? JSON.parse(localStorage.pages) : []
        } catch (e) {
          return []
        }
      }
      this.pagesData = fromCache()
      try {
        const res = await this.getPages()
        if (!this.alive) return
        const pages = res && res.body && res.body.pages
        if (Array.isArray(pages)) {
          this.pagesData = pages
          localStorage.setItem('pages', JSON.stringify(pages))
        }
      } catch (e) {
        console.error('[overview] pages', e)
      }
    },

    async loadOverview(refresh = false) {
      const requestId = ++this.overviewRequestId
      const { since, until } = periodRange(this.period)
      const params = new URLSearchParams()
      if (since !== null) {
        params.set('since', since)
        params.set('until', until)
      }
      params.set('timezone', new Date().getTimezoneOffset())
      if (refresh) params.set('refresh', '1')

      this.overviewLoading = true
      this.overviewError = false
      try {
        const res = await this.$http.get(`${VUE_APP_API_HOST}/reporting/overview?${params.toString()}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        if (!this.alive || requestId !== this.overviewRequestId) return
        this.overview = res.body
        writePeriod(this.cacheUserId(), this.period, res.body)
        if (this.fallbackToAllTimeIfIdle()) return
        this.loadLastPostStats(requestId)
      } catch (e) {
        if (!this.alive || requestId !== this.overviewRequestId) return
        console.error('[overview] reporting', e)
        this.overviewError = true
      } finally {
        if (this.alive && requestId === this.overviewRequestId) this.overviewLoading = false
      }
    },

    // Compte peu actif : sans choix mémorisé et sans post sur 30 jours, on affiche « depuis le début »
    fallbackToAllTimeIfIdle() {
      if (this.periodWasStored || this.period.key !== DEFAULT_PERIOD.key) return false
      const pub = this.overview && this.overview.publishing
      if (!pub || pub.posted > 0) return false
      this.periodWasStored = true
      this.period = { key: 'all' }
      this.loadOverview()
      return true
    },

    /**
     * Stats en direct des dernières publications (POST /insights), une à une comme l'ancien overview
     */
    async loadLastPostStats(requestId) {
      const posts = (this.overview && this.overview.last_posts && this.overview.last_posts.posts) || []
      // Stats déjà connues (cache) gardées à l'écran pendant leur rafraîchissement
      const previous = this.lastPostStats || {}
      const stats = {}
      posts.forEach(post => {
        const key = this.postKey(post)
        stats[key] = this.canFetchInsights(post) ? { loading: true, metrics: previous[key] ? previous[key].metrics : null } : null
      })
      this.lastPostStats = stats

      for (const post of posts) {
        if (!this.canFetchInsights(post)) continue
        const key = this.postKey(post)
        let metrics = null
        try {
          const res = await this.getInsights(insightsRequest(post))
          const embed = res && res.body && res.body.posts && res.body.posts[0] && res.body.posts[0].page && res.body.posts[0].page[0]
          metrics = normalizeInsights(post.network, embed, (k) => this.$t(k))
        } catch (e) {
          metrics = null
        }
        if (!this.alive || requestId !== this.overviewRequestId) return
        this.$set(this.lastPostStats, key, { loading: false, metrics })
      }
      const persisted = Object.fromEntries(Object.entries(this.lastPostStats).filter(([, s]) => s && s.metrics).map(([k, s]) => [k, s.metrics]))
      writeSection(this.cacheUserId(), 'stats', persisted)
    },

    postKey(post) {
      return `${post.news_id}-${post.bot_uid || post.page_id}`
    },

    // Groupes Facebook / Instagram exclus, comme dans l'ancien overview
    canFetchInsights(post) {
      if (!post.bot_uid || !post.network_post_id || !INSIGHTS_NETWORKS.includes(post.network)) return false
      const page = this.pagesData.find(p => p.id === post.page_id)
      return !(page && page.isGroup)
    },

    async loadPostsAlerts() {
      const pageIds = this.pagesData.map(p => p.id).filter(Boolean)
      if (!pageIds.length) return
      const now = moment()

      this.upcomingLoading = true
      const until = now.clone().add(7, 'days')
      // Pages pouvant faire échouer une publication planifiée : sans API (déconnectée / natif seul) ou jeton qui expire
      const riskPageIds = this.pagesData
        .filter(p => [0, 2].includes(Number(p.bot_status)) || (p.story_config && p.story_config.onlyConciergerie))
        .map(p => p.id)
      const [scheduled, drafts, riskScheduled] = await Promise.all([
        this.fetchAllPosts(pageIds, { statuses: 'scheduled,draft', startDate: now.toDate(), endDate: until.toDate() }),
        this.canEdit ? this.fetchAllPosts(pageIds, { statuses: 'draft' }) : [],
        riskPageIds.length ? this.fetchAllPosts(riskPageIds, { statuses: 'scheduled', startDate: now.toDate() }) : []
      ])
      if (!this.alive) return

      // Posts planifiés et brouillons datés des 7 prochains jours (l'API arrondit endDate à la fin de journée)
      this.upcoming = scheduled
        .filter(post => this.isUpcomingCandidate(post))
        .map(post => this.toUpcomingItem(post))
        .filter(item => item && moment(item.date).isAfter(now) && !moment(item.date).isAfter(until))
        .sort((a, b) => (a.date < b.date ? -1 : 1))
      this.upcomingLoading = false


      const isScheduledPost = post => this.isUpcomingCandidate(post) && !(post.status === 'draft' || (post.page && post.page[0] && post.page[0].isDraft))
      const dedupe = items => Object.values(items.reduce((acc, item) => ({ ...acc, [item.key]: item }), {}))
      // Publications planifiées (statut 1, hors brouillons) : pages à risque (toutes dates) + planifiées non validées (7 jours)
      this.riskCandidates = dedupe([
        ...riskScheduled.filter(isScheduledPost).map(post => this.toPostItem(post)),
        ...scheduled.filter(isScheduledPost).map(post => this.toPostItem(post)).filter(item => item && item.validation_pending)
      ].filter(item => item && item.date && moment(item.date).isAfter(now)))

      const userId = this.currentUser && this.currentUser.id
      const embedOf = d => (d.page && d.page[0]) || {}
      const byDate = (a, b) => (a.date || '9999') < (b.date || '9999') ? -1 : 1
      // En attente de validation : validation requise et pas encore validée (brouillons + planifiées des 7 jours)
      this.validationPosts = dedupe([...drafts, ...scheduled.filter(isScheduledPost)]
        .filter(d => this.isValidationPending(embedOf(d)))
        .map(d => this.toPostItem(d))
        .filter(Boolean)).sort(byDate)
      this.assignedPosts = drafts.filter(d => {
        const affected = embedOf(d).affectedTo || {}
        const users = affected.users || (affected.user ? [affected.user] : [])
        return users.some(u => u && (u.id || u) === userId)
      }).map(d => this.toPostItem(d)).filter(Boolean).sort(byDate)
      this.sideLoaded = true

      // Pages non stockées dans le cache (déjà dans localStorage.pages), retrouvées par page_id à l'hydratation
      writeSection(this.cacheUserId(), 'side', {
        upcoming: this.upcoming.map(({ page, ...item }) => item),
        riskCandidates: this.riskCandidates.map(({ page, ...item }) => item),
        validationPosts: this.validationPosts.map(({ page, ...item }) => item),
        assignedPosts: this.assignedPosts.map(({ page, ...item }) => item)
      })
    },

    /**
     * Toutes les pages de résultats de /posts/v2 (curseur), avec un garde-fou sur le nombre d'appels
     */
    async fetchAllPosts(pageIds, options) {
      const all = []
      let cursor = null
      for (let i = 0; i < 25; i++) {
        const res = await this.postsByPagesV2(pageIds, { ...options, cursor, limit: 200 })
        const body = (res && res.body) || {}
        if (Array.isArray(body.posts)) all.push(...body.posts)
        if (!body.hasMore || !body.nextCursor || !this.alive) break
        cursor = body.nextCursor
      }
      return all
    },

    // Planifié = statut 1 (le 7 « en cours de création » est exclu) ; brouillon = draft PG ou Mongo, hors supprimés
    isUpcomingCandidate(post) {
      const embed = post.page && post.page[0]
      if (!embed) return false
      if (post.status === 'draft' || embed.isDraft) return Number(embed.status) !== -1
      return Number(embed.status) === 1
    },

    toUpcomingItem(post) {
      const item = this.toPostItem(post)
      return item && item.date ? item : null
    },

    // Item de publication commun (À venir, À traiter), avec les infos du lien profond Posts2 ; date facultative (brouillons)
    toPostItem(post) {
      const embed = post.page && post.page[0]
      if (!embed) return null
      const date = embed.scheduled_date || post.scheduled_date || null
      const bot = (post._bots || []).find(b => b.uid === embed.id) || (post._bots || [])[0]
      const pageId = bot && (bot.page && bot.page.id ? bot.page.id : bot.page)
      const page = this.pagesData.find(p => p.id === pageId) || null
      const text = embed.description || post.title || post.description || ''
      return {
        key: `${post._id || post.id}-${embed.id}`,
        // Lien profond Posts2 (cf. openPublishedPost)
        news_id: post._id || post.id,
        is_draft: post.status === 'draft' || !!embed.isDraft,
        page_id: pageId,
        bot_id: bot && bot.id,
        date,
        page,
        text: String(text).slice(0, 160),
        image: embed.enclosure || post.enclosure || null,
        validation_pending: this.isValidationPending(embed)
      }
    },

    loadInboxCount() {
      const user = this.currentUser
      if (!user || !this.inboxAllowed) return
      this.countWaitingEvents(user.account).then(res => {
        if (!this.alive || !res || !res.body || !res.body.pages) return
        const allowed = p => ((!user.pages_access || !user.pages_access.length) && user.admin) || (user.pages_access && user.pages_access.includes(p.id))
        const pages = res.body.pages.filter(allowed)
        this.inboxCount = pages.reduce((s, p) => s + (p.count || 0), 0)
        this.inboxCounts = pages.reduce((acc, p) => ({ ...acc, [p.id]: p.count || 0 }), {})
        writeSection(this.cacheUserId(), 'inbox', { count: this.inboxCount, counts: this.inboxCounts })
      })
    },

    selectPeriod(period) {
      this.period = period
      this.periodWasStored = true
      storePeriod(period)
      // Période déjà consultée : affichage immédiat depuis le cache pendant le rechargement
      const cached = readPeriod(this.cacheUserId(), period)
      if (cached) this.overview = cached
      this.loadOverview()
    },

    onCustomPeriod({ dateStart, dateEnd }) {
      if (!dateStart || !dateEnd) return
      this.selectPeriod({ key: 'custom', start: dateStart, end: dateEnd })
    },

    // Reconnexion directe uniquement pour les pages API classiques ; conciergerie / Bluesky passent par la section pages
    /**
     * Page sans connexion utilisable : ni API (bot_status 1/2/3), ni natif connecté.
     * Même règle que l'état des pages (OverviewPages.isDisconnected)
     */
    // Raison pour laquelle une publication planifiée risque de ne pas partir (null si aucune)
    riskReason(item) {
      if (!item || item.is_draft) return null
      const page = item.page || this.pagesData.find(p => p.id === item.page_id)
      if (!page) return null
      if (this.isPageUnusable(page)) return 'disconnected'
      if (Number(page.bot_status) === 2 && page.expires_at && item.date && new Date(item.date) > new Date(page.expires_at)) return 'expiring'
      // Assignée avec validation requise, non validée, et qui doit partir dans moins de 24 h : sera bloquée à l'envoi
      if (item.validation_pending && item.date && new Date(item.date).getTime() - Date.now() <= 24 * 3600 * 1000) return 'validation'
      return null
    },

    /**
     * Validation requise (affectedTo.optionValidation) et pas encore donnée : wait_validation est renseigné
     * une fois la publication validée (même règle que validationStatus du drawer)
     */
    isValidationPending(embed) {
      const affected = (embed && embed.affectedTo) || {}
      const required = affected.optionValidation === true || affected.optionValidation === 'true'
      const validated = embed && embed.wait_validation !== undefined && embed.wait_validation !== null && embed.wait_validation !== 'null' && embed.wait_validation !== ''
      return required && !validated
    },

    isPageUnusable(page) {
      if (!page) return false
      const stat = ((this.overview && this.overview.per_page) || []).find(p => p.id === page.id)
      const native = stat && stat.native
      const nativeOnly = !!((native && native.only) || (page.story_config && page.story_config.onlyConciergerie))
      if (!native) return Number(page.bot_status) === 0 && !nativeOnly
      const apiOk = !nativeOnly && [1, 2, 3].includes(Number(page.bot_status))
      return !apiOk && native.state !== 'connected'
    },

    canReconnectInline(page) {
      if (!page || ['bluesky', 'oneSignal', 'wonderpush', 'telegram', 'whatsapp'].includes(page.network)) return false
      if (page.isGroup && page.network === 'instagram') return false
      if (page.story_config && page.story_config.onlyConciergerie) return false
      return this.checkLock(page.network)
    },

    reconnect(page) {
      if (!this.canReconnectInline(page)) {
        this.go({ name: 'pages' })
        return
      }
      this.confirmRefresh(page)
    },

    /**
     * Point d'entrée unique pour ouvrir un post depuis l'overview (dernières publications, à venir, top posts).
     * Ouvre le post dans Posts2 (mêmes infos que « Copier le lien » du drawer), passées en params
     * du router : rien dans l'URL, donc le post ne se rouvre pas au rechargement
     */
    openPublishedPost(post) {
      if (!post.news_id || !post.page_id || !post.bot_id) {
        this.openPost(post)
        return
      }
      localStorage.setItem('section', 'posts2')
      const params = { post_id: String(post.news_id), page_id: String(post.page_id), bot_id: String(post.bot_id) }
      if (post.is_draft) params.is_draft = true
      this.$router.push({ name: 'posts2', params })
    },

    /**
     * Ouvre le post sur le réseau, même logique que showOriginalPost du drawer
     */
    async openOnNetwork(post) {
      const id = post.network_post_id
      let url = null
      try {
        switch (post.network) {
          case 'facebook':
            url = `https://www.facebook.com/${id}`
            break
          case 'twitter':
            url = `https://www.twitter.com/${post.page_name}/status/${id}`
            break
          case 'linkedin':
            if (post.post_number_bis) {
              url = `https://www.linkedin.com/feed/update/${post.post_number_bis}`
            } else {
              const res = await this.getLinkedinPost(post.page_id, id)
              const body = (res && res.body) || {}
              if (body.success && body.post) url = `https://www.linkedin.com/feed/update/${body.post.activity || body.post.id}`
            }
            break
          case 'pinterest':
            url = `https://www.pinterest.fr/pin/${id}`
            break
          case 'google':
            if (post.post_number_bis) {
              url = post.post_number_bis
            } else {
              const res = await this.getGooglePost(post.page_id, id)
              const body = (res && res.body) || {}
              url = body.success && body.searchUrl ? body.searchUrl : `https://www.google.com/search?q=${id}`
            }
            break
          case 'youtube':
            url = `https://www.youtube.com/shorts/${id}`
            break
          case 'threads':
            url = `https://www.threads.net/@${post.page_name}`
            break
          case 'bluesky':
            url = `https://bsky.app/profile/${post.page_name}/post/${String(id).split('/').pop()}`
            break
        }
      } catch (e) {
        console.error('[overview] network link', e)
      }
      if (url) Popup.open(url, '', 900, 700)
    },

    // Actions par page : la page passe en params du router (rien dans l'URL, sélection enregistrée non écrasée)
    createPostForPage(page) {
      this.go({ name: 'postCreator', params: { pageId: String(page.id) } })
    },

    // Reporting sur la page et la période de l'overview ; « depuis le début » est ramené à 12 mois (calcul jour par jour)
    openReportingForPage(page) {
      if (!isReportingEligible(page)) return
      const { since, until } = periodRange(this.period)
      const end = until ? moment.unix(until) : moment()
      const start = since ? moment.unix(since) : moment().subtract(12, 'months')
      this.go({ name: 'reporting', params: { since: start.format('YYYY-MM-DD'), until: end.format('YYYY-MM-DD'), pageIds: String(page.id) } })
    },

    openInboxForPage(page) {
      this.go({ name: 'moderation', params: { moderation: String(page.id) } })
    },

    /**
     * 20 derniers événements en attente (statuts 0 et 2, comme le compteur) : l'API pagine par 10
     */
    async loadInboxEvents() {
      // Une seule mise à jour réseau par visite ; le cache éventuel reste affiché pendant ce temps
      if (this.inboxEventsLoading || this.inboxEventsFresh) return
      const pages = this.pagesData.filter(p => p && p.id)
      if (!pages.length) return
      this.inboxEventsLoading = true
      try {
        const filter = { status: [0, 2] }
        // from en chaîne (« 0 » inclus) : sans from, l'engine relance une synchro TikTok, inutile ici
        const responses = await Promise.all(['0', '10'].map(from => this.listInboxEvents(pages, from, filter)))
        if (!this.alive) return
        const events = responses.flatMap(res => (res && res.body && Array.isArray(res.body.events) ? res.body.events : []))
        const seen = new Set()
        this.inboxEvents = events
          .filter(e => e && e._id && !seen.has(e._id) && seen.add(e._id))
          .map(e => this.toInboxItem(e))
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, 20)
        this.inboxEventsFresh = true
        writeSection(this.cacheUserId(), 'inboxEvents', this.inboxEvents.map(({ page, ...item }) => item))
      } catch (e) {
        if (!this.inboxEvents) this.inboxEvents = []
      } finally {
        this.inboxEventsLoading = false
      }
    },

    // Événement Inbox -> ligne de pop-in ; contenu lu comme dans moderation/ListInbox.vue
    toInboxItem(e) {
      const data = e.data || {}
      const value = (data.changes && data.changes.value) || {}
      const user = e.user || {}
      const page = this.pagesData.find(p => String(p.network_id) === String(e.page) && p.network === e.network) || null
      const text = (data.comment && data.comment.text) ||
        value.message || value.text || value.review_text ||
        (data.messaging && data.messaging.message && data.messaging.message.text) ||
        (data.decoratedGeneratedActivity && data.decoratedGeneratedActivity.comment && data.decoratedGeneratedActivity.comment.text) ||
        (data.messages && data.messages.elements && data.messages.elements[0] && data.messages.elements[0].body && data.messages.elements[0].body.text) || ''
      const author = user.localizedName || user.name || user.username ||
        (value.from && (value.from.username || value.from.name)) ||
        (data.comment && data.comment.user && data.comment.user.name) || ''
      // date en secondes Unix (cf. ListInbox.formatDate)
      const timestamp = Number(e.date) < 1e12 ? Number(e.date) * 1000 : Number(e.date)
      const typeKey = `dash.inbox.type.${e.type}`
      return {
        key: e._id,
        object_id: e.object_id,
        page_id: page ? page.id : null,
        page,
        network: e.network,
        type: this.$te(typeKey) ? this.$t(typeKey) : e.type,
        raw_type: e.type,
        author: String(author),
        text: String(typeof text === 'string' ? text : '').replace(/<[^>]*>/g, '').slice(0, 160),
        timestamp: timestamp || 0
      }
    },

    openInboxEvent(item) {
      if (!item.page_id) {
        this.go({ name: 'moderation' })
        return
      }
      const params = { moderation: String(item.page_id) }
      if (item.object_id) params.object_id = String(item.object_id)
      this.go({ name: 'moderation', params })
    },

    openPost(item) {
      const date = item.date || item.posted_date
      this.go({ name: 'posts2', tab: 'planner', params: date ? { date: moment(date).toISOString(), scrollToHour: true } : {} })
    },

    go(target) {
      if (!target || !target.name) return
      if (target.tab) {
        try {
          localStorage.setItem('posts/tab', target.tab)
        } catch (e) {
          // stockage indisponible
        }
      }
      localStorage.setItem('section', target.name)
      this.$router.push({ name: target.name, params: target.params || {} })
    }
  }
}
</script>

<style>
/* DA alignée sur Reporting / Posts2 : primaire #6B1F4D, teinte #F1EDF5, texte #121826 */
.section-overview.ov {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 40px;
  color: #121826;
}

/* La règle globale « .dash > div button » impose fond, hauteur et casse : neutralisée pour les boutons ov-* */
.section-overview.ov.ov button[class*="ov-"] {
  height: auto !important;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font-size: inherit !important;
  font-weight: inherit !important;
  line-height: normal;
  box-shadow: none !important;
  text-transform: none;
  cursor: pointer;
}
.section-overview.ov.ov button[class*="ov-"] span,
.section-overview.ov.ov button[class*="ov-"] p {
  text-transform: none;
}
.section-overview.ov.ov button[class*="ov-"]:disabled {
  opacity: 0.45;
  cursor: default;
}

/* En-tête (titre + actions rapides, comme l'Inbox) */
.ov-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}
.ov-title {
  font-size: 27.2px;
  font-weight: 700;
  color: #121826;
  margin: 0;
}
.ov-subtitle {
  margin-top: 4px;
  font-size: 13.6px;
  color: #555e75;
}
.ov-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}
.section-overview.ov.ov button.ov-action {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 35.2px !important;
  padding: 0 14px;
  border-radius: 5.6px;
  background: #f1edf5;
  border: 1.6px solid #f1edf5;
  color: #6b1f4d;
  font-size: 13.6px !important;
  font-weight: 600 !important;
  white-space: nowrap;
  transition: all 0.15s;
}
.section-overview.ov.ov button.ov-action:hover {
  background: #e7dcef;
  border-color: #e7dcef;
}
.section-overview.ov.ov button.ov-action .material-icons {
  font-size: 18px;
}
.section-overview.ov.ov button.ov-action--primary,
.section-overview.ov.ov button.ov-action--primary:hover {
  background: #6b1f4d;
  border-color: #6b1f4d;
  color: #fff;
}
.section-overview.ov.ov button.ov-action--primary:hover {
  opacity: 0.88;
}
.ov-action-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: #6b1f4d;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
}

/* État des pages | État des sources : même hauteur (hauteur max commune), les listes défilent à l'intérieur */
.section-overview.ov .ov-columns--equal {
  align-items: stretch;
}
.section-overview.ov .ov-columns--equal > .ov-card {
  display: flex;
  flex-direction: column;
  max-height: 660px;
  min-height: 0;
}
.section-overview.ov .ov-columns--equal .pages-list,
.section-overview.ov .ov-columns--equal .src-list {
  flex: 1;
  min-height: 0;
  max-height: none;
}

/* Santé du compte : séparée des statistiques, comme le bloc Statistiques */
.ov-health {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 20px;
  border-top: 1px solid #f1edf5;
}
.ov-section-title {
  margin-bottom: 0;
}

/* Bloc Statistiques : en-tête (titre + période) puis contenus qui dépendent de la période */
.ov-stats {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 20px;
  border-top: 1px solid #f1edf5;
}
.ov-stats-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}
.ov-stats-head .ov-block-title {
  margin-bottom: 0;
}
/* Barre de période (carte toolbar du Reporting), alignée à droite du titre */
.ov-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  background: #fff;
  border-radius: 5.6px;
  padding: 6px 10px;
  border: 1px solid #f1edf5;
  box-shadow: 0 2px 12px rgba(107, 31, 77, 0.06);
}

/* Le bandeau « À traiter » passe au-dessus des blocs suivants : ses pop-in ne sont pas recouverts
   (les animations d'apparition créent des contextes d'empilement sur les blocs frères) */
.section-overview.ov > .overview-alerts {
  position: relative;
  z-index: 20;
}

/* Pastilles « À traiter » (style .btn-radius de Posts2, teintes de statut) */
.section-overview.ov.ov button.ov-alert-chip {
  --chip: #548eff;
  --chip-bg: #f0f3ff;
  --chip-border: #dce2ed;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px !important;
  padding: 0 10px 0 12px;
  border-radius: 17.6px;
  background: var(--chip-bg);
  border: 1.6px solid var(--chip-border);
  color: #121826;
  font-size: 12.8px !important;
  font-weight: 500 !important;
  white-space: nowrap;
  transition: all 0.15s;
}
.section-overview.ov.ov button.ov-alert-chip--failed { --chip: #e04848; --chip-bg: #fdf2f2; --chip-border: #ffdede; }
.section-overview.ov.ov button.ov-alert-chip--warning { --chip: #d96a0c; --chip-bg: #fff2e6; --chip-border: #ffe8d4; }
.section-overview.ov.ov button.ov-alert-chip--pending { --chip: #8b55c0; --chip-bg: #faf4fd; --chip-border: #ece5f7; }
.section-overview.ov.ov button.ov-alert-chip .material-icons {
  font-size: 16px;
  color: var(--chip);
}
.section-overview.ov.ov button.ov-alert-chip strong {
  font-weight: 700;
  color: var(--chip);
}
.section-overview.ov.ov button.ov-alert-chip .ov-alert-chip-caret {
  color: #9ba3b7;
}
.section-overview.ov.ov button.ov-alert-chip:hover,
.section-overview.ov.ov button.ov-alert-chip.active {
  border-color: var(--chip);
}
.ov-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.section-overview.ov.ov button.ov-preset {
  padding: 5px 14px;
  border-radius: 20px;
  border: 1px solid #e0d0ea;
  background: #fff;
  color: #6b1f4d;
  font-size: 13px !important;
  font-weight: 500 !important;
  transition: all 0.15s;
}
.section-overview.ov.ov button.ov-preset:hover,
.section-overview.ov.ov button.ov-preset.active {
  background: #6b1f4d;
  border-color: #6b1f4d;
  color: #fff;
}
.ov-period-custom {
  border-radius: 8px;
}
.ov-period-custom.active {
  box-shadow: 0 0 0 1.6px #6b1f4d;
}
/* Motion design (test) : apparition en fondu + légère montée, en cascade ; désactivé si l'utilisateur réduit les animations */
@keyframes ov-rise {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: none; }
}
.section-overview.ov > *,
.section-overview.ov .ov-card,
.section-overview.ov .kpi-tile,
.section-overview.ov .alert-card,
.section-overview.ov .top-card,
.section-overview.ov .ov-action {
  animation: ov-rise 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
}
.section-overview.ov > *:nth-child(2) { animation-delay: 0.05s; }
.section-overview.ov > *:nth-child(3) { animation-delay: 0.1s; }
.section-overview.ov > *:nth-child(4) { animation-delay: 0.15s; }
.section-overview.ov > *:nth-child(5) { animation-delay: 0.2s; }
.section-overview.ov > *:nth-child(6) { animation-delay: 0.25s; }
.section-overview.ov .ov-columns > :nth-child(2),
.section-overview.ov .kpi-grid > :nth-child(2),
.section-overview.ov .alerts-grid > :nth-child(2),
.section-overview.ov .top-grid > :nth-child(2),
.section-overview.ov .ov-actions > :nth-child(2) { animation-delay: 0.06s; }
.section-overview.ov .kpi-grid > :nth-child(3),
.section-overview.ov .alerts-grid > :nth-child(3),
.section-overview.ov .top-grid > :nth-child(3),
.section-overview.ov .ov-actions > :nth-child(3) { animation-delay: 0.12s; }
.section-overview.ov .kpi-grid > :nth-child(4),
.section-overview.ov .alerts-grid > :nth-child(4),
.section-overview.ov .ov-actions > :nth-child(4) { animation-delay: 0.18s; }
.section-overview.ov .kpi-grid > :nth-child(n+5),
.section-overview.ov .alerts-grid > :nth-child(n+5),
.section-overview.ov .ov-actions > :nth-child(n+5) { animation-delay: 0.24s; }
.section-overview.ov .ov-card,
.section-overview.ov .kpi-tile,
.section-overview.ov .top-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.section-overview.ov .kpi-tile:hover,
.section-overview.ov .top-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(107, 31, 77, 0.12);
}
@media (prefers-reduced-motion: reduce) {
  .section-overview.ov *,
  .section-overview.ov > * {
    animation: none !important;
    transition: none !important;
  }
}
/* Barre de défilement discrète commune à tous les blocs de l'overview (pages, à venir, dernières publications…) */
.section-overview.ov * {
  scrollbar-width: thin;
  scrollbar-color: #e0d0ea transparent;
}
.section-overview.ov *::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.section-overview.ov *::-webkit-scrollbar-track {
  background: transparent;
}
.section-overview.ov *::-webkit-scrollbar-thumb {
  background: #e0d0ea;
  border-radius: 3px;
}
.section-overview.ov *::-webkit-scrollbar-thumb:hover {
  background: #ab6e93;
}
.ov-help-icon {
  font-size: 13px;
  color: #9ba3b7;
  margin-left: 4px;
  vertical-align: middle;
  cursor: help;
}
.ov-help-title {
  display: inline-flex;
  align-items: center;
}
.ov-updating {
  margin-left: auto;
  font-size: 12px;
  color: #9ba3b7;
}
.ov-updating + .ov-toolbar-refresh {
  margin-left: 0;
}
.ov-refreshing {
  opacity: 0.6;
  transition: opacity 0.2s;
}
.ov-toolbar-refresh {
  margin-left: auto;
}
.section-overview.ov.ov button.ov-refresh {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 35.2px;
  height: 35.2px !important;
  border-radius: 8px;
  border: 1.5px solid #e0d0ea;
  background: #fff;
  color: #6b1f4d;
  transition: background 0.15s;
}
.section-overview.ov.ov button.ov-refresh:hover {
  background: #f1edf5;
}

/* Blocs (cartes .reporting-section) */
.ov-columns--wide-left {
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr) !important;
}
@media (max-width: 1100px) {
  .ov-columns--wide-left { grid-template-columns: 1fr !important; }
}
.ov-columns {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}
@media (max-width: 1100px) {
  .ov-columns { grid-template-columns: 1fr; }
}
.ov-card {
  background: #fff;
  border: 1px solid #f1edf5;
  border-radius: 5.6px;
  padding: 20px 24px;
  box-shadow: 0 2px 5.6px rgba(107, 31, 77, 0.06);
}
.ov-block-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 18px;
}
.ov-title-icon,
.ov-block-title > .material-icons {
  font-size: 22px;
  color: #6b1f4d;
}
.ov-block-title h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #121826;
}
.ov-chip-muted {
  font-size: 12px;
  color: #9ba3b7;
  background: #f1edf5;
  border-radius: 20px;
  padding: 2px 10px;
}
.ov-block-link {
  margin-left: auto;
}

/* Boutons partagés avec les sous-composants */
.section-overview.ov.ov button.ov-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 35.2px !important;
  padding: 0 14px;
  border-radius: 5.6px;
  font-size: 13.6px !important;
  font-weight: 500 !important;
}
.section-overview.ov.ov button.ov-btn--primary {
  background: #6b1f4d;
  border: 1.6px solid #6b1f4d;
  color: #fff;
}
.section-overview.ov.ov button.ov-btn--primary:hover {
  opacity: 0.88;
}
.section-overview.ov.ov button.ov-arrow-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  border-radius: 8px;
  border: 1.5px solid #e0d0ea;
  background: #fff;
  color: #6b1f4d;
  font-size: 13px !important;
  font-weight: 600 !important;
  transition: background 0.15s;
}
.section-overview.ov.ov button.ov-arrow-link:hover {
  background: #f1edf5;
}
.section-overview.ov.ov button.ov-arrow-link .material-icons {
  font-size: 16px;
}
.section-overview.ov.ov button.ov-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  height: 26px !important;
  padding: 0 10px;
  border-radius: 17.6px;
  background: #f1edf5;
  border: 1.6px solid #f1edf5;
  color: #6b1f4d;
  font-size: 12px !important;
  font-weight: 600 !important;
  transition: all 0.15s;
}
.section-overview.ov.ov button.ov-pill:hover {
  background: #6b1f4d;
  border-color: #6b1f4d;
  color: #fff;
}
.section-overview.ov.ov button.ov-pill .material-icons {
  font-size: 14px;
}
.section-overview.ov.ov button.ov-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px !important;
  border-radius: 5.6px;
  background: #f1edf5;
  color: #6b1f4d;
  transition: all 0.15s;
}
.section-overview.ov.ov button.ov-icon-btn:hover {
  background: #6b1f4d;
  color: #fff;
}
.section-overview.ov.ov button.ov-icon-btn--danger {
  background: #fdf0f0;
  color: #d93025;
}
.section-overview.ov.ov button.ov-icon-btn--danger:hover {
  background: #d93025;
  color: #fff;
}
.section-overview.ov.ov button.ov-icon-btn--badge {
  position: relative;
}
.ov-icon-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: #6b1f4d;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;
}
.section-overview.ov.ov button.ov-icon-btn .material-icons {
  font-size: 18px;
}

.ov-empty {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  color: #9ba3b7;
  padding: 8px 0;
}
.ov-error {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #d93025;
}
.ov-skeleton-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ov-skeleton-line {
  height: 48px;
  border-radius: 10px;
  background: #f1edf5;
}
.ov .spin {
  animation: ov-spin 1s linear infinite;
}
@keyframes ov-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@media (max-width: 700px) {
  .ov-actions { width: 100%; }
  .ov-toolbar-refresh { margin-left: 0; }
}
</style>
